/**
 * Moteur CAT (Computer Adaptive Testing) pour WASPL
 * 
 * Logique de fonctionnement :
 * - Démarrage au niveau médian (3=2, 4=2, 5=3)
 * - Une seule question à la fois
 * - Réussite → niveau supérieur
 * - Échec → niveau inférieur  
 * - Aucune question ne doit être répétée
 * - Arrêt si seuil d'échec atteint au niveau 1
 * - Arrêt si seuil de confirmation atteint
 * - Mémorisation complète du parcours pour le rapport final
 */

export class CATEngine {
  constructor(testData) {
    console.log('🔧 CATEngine constructor called with:', {
      hasTestData: !!testData,
      hasElements: !!(testData?.elements),
      elementsCount: testData?.elements?.length || 0,
      hasLevels: !!(testData?.levels),
      levelsCount: testData?.levels?.length || 0
    });
    
    if (!testData) {
      throw new Error('CATEngine: testData is required');
    }
    
    this.testData = testData;
    this.elements = Array.isArray(testData.elements) ? testData.elements : [];
    this.catParameters = testData.CATParameters || {};
    
    if (this.elements.length === 0) {
      console.warn('⚠️ CATEngine: No elements provided, test may not work correctly');
    }
    
    // Initialiser les niveaux - créer une structure de base si elle n'existe pas
    this.levels = this.initializeLevels(testData);
    
    // Paramètres CAT par défaut
    this.confirmationThreshold = this.catParameters.confirmation || 3;
    this.failureThreshold = this.catParameters.echec || 3;
    
    // État du test
    this.currentLevel = this.calculateStartingLevel();
    this.questionHistory = []; // Historique complet du parcours
    this.usedQuestions = new Set(); // Questions déjà posées
    this.levelSuccesses = new Map(); // Compteur de réussites par niveau
    this.levelFailures = new Map(); // Compteur d'échecs par niveau
    this.isCompleted = false;
    this.completionReason = null;
    
    console.log('🐈 CAT Engine initialized:', {
      startingLevel: this.currentLevel,
      totalLevels: this.levels.length,
      totalItems: this.elements.length,
      confirmationThreshold: this.confirmationThreshold,
      failureThreshold: this.failureThreshold
    });
  }

  /**
   * Initialise la structure des niveaux
   * Si testData.levels existe, l'utilise
   * Sinon, crée une structure de base à partir des éléments
   */
  initializeLevels(testData) {
    // Si la structure levels existe déjà, l'utiliser
    if (testData.levels && testData.levels.length > 0) {
      console.log('📊 Using existing levels structure:', testData.levels.length, 'levels');
      return testData.levels;
    }

    // Sinon, créer une structure de base
    console.log('🔧 Creating default levels structure from elements');
    
    // Analyser les difficultés des éléments
    if (!Array.isArray(this.elements) || this.elements.length === 0) {
      console.warn('⚠️ No elements available for level creation, using default 3 levels');
      const levels = [];
      for (let i = 1; i <= 3; i++) {
        levels.push({
          id: i,
          name: `Level ${i}`,
          difficulty: i,
          items: []
        });
      }
      return levels;
    }
    
    const difficulties = this.elements
      .map(el => el.difficulty || el.difficulty_rating || 3)
      .filter(diff => diff !== undefined && typeof diff === 'number');
    
    if (difficulties.length === 0) {
      console.warn('⚠️ No valid difficulties found in elements, assigning default values');
      // Assigner des difficultés par défaut
      this.elements.forEach((el, index) => {
        el.difficulty = Math.floor(index / Math.ceil(this.elements.length / 3)) + 1;
      });
      difficulties.push(...this.elements.map(el => el.difficulty));
    }
    
    const minDiff = Math.min(...difficulties, 1);
    const maxDiff = Math.max(...difficulties, 5);
    const levelCount = Math.max(3, maxDiff - minDiff + 1);
    
    // Créer les niveaux
    const levels = [];
    for (let i = 1; i <= levelCount; i++) {
      levels.push({
        id: i,
        name: `Level ${i}`,
        difficulty: i,
        items: this.elements.filter(el => 
          (el.difficulty || el.difficulty_rating || 3) === i
        ).map(el => ({ el_ID: el.el_ID }))
      });
    }
    
    console.log('✅ Created', levels.length, 'default levels with', this.elements.length, 'total elements');
    return levels;
  }

  /**
   * Calcule le niveau de départ médian selon les règles :
   * 3 niveaux = 2, 4 niveaux = 2, 5 niveaux = 3
   */
  calculateStartingLevel() {
    const levelCount = this.levels.length;
    
    if (levelCount <= 3) return 2;
    if (levelCount === 4) return 2;
    if (levelCount >= 5) return 3;
    
    return Math.ceil(levelCount / 2);
  }

  /**
   * Récupère la prochaine question pour le niveau actuel
   * @returns {Object|null} La prochaine question ou null si aucune disponible
   */
  getNextQuestion() {
    if (this.isCompleted) {
      console.log('🛑 Test already completed');
      return null;
    }

    // Trouver les questions disponibles pour le niveau actuel
    const availableQuestions = this.getAvailableQuestionsForLevel(this.currentLevel);
    
    if (availableQuestions.length === 0) {
      console.warn(`⚠️ No available questions for level ${this.currentLevel}`);
      // Essayer de trouver des questions dans les niveaux adjacents
      return this.getQuestionFromAdjacentLevels();
    }

    // Sélectionner une question aléatoirement si randomisé, sinon prendre la première
    const question = this.catParameters.randomizedItemOrder 
      ? availableQuestions[Math.floor(Math.random() * availableQuestions.length)]
      : availableQuestions[0];

    // Marquer la question comme utilisée
    this.usedQuestions.add(question.el_ID);

    console.log(`🎯 Selected question for level ${this.currentLevel}:`, {
      questionId: question.el_ID,
      difficulty: question.difficulty,
      usedQuestions: this.usedQuestions.size
    });

    return question;
  }

  /**
   * Trouve les questions disponibles (non utilisées) pour un niveau donné
   */
  getAvailableQuestionsForLevel(level) {
    return this.elements.filter(element => {
      // Vérifier que la question n'a pas été utilisée
      if (this.usedQuestions.has(element.el_ID)) return false;
      
      // Vérifier que la question correspond au niveau
      if (element.difficulty === level) return true;
      
      // Si pas de difficulté explicite, utiliser l'index du niveau
      const elementLevel = this.levels.findIndex(l => 
        l.items && l.items.some(item => item.el_ID === element.el_ID)
      ) + 1;
      
      return elementLevel === level;
    });
  }

  /**
   * Essaie de trouver une question dans les niveaux adjacents
   */
  getQuestionFromAdjacentLevels() {
    const maxLevel = this.levels.length;
    
    // Essayer les niveaux adjacents (±1, ±2, etc.)
    for (let offset = 1; offset <= maxLevel; offset++) {
      // Niveau supérieur
      if (this.currentLevel + offset <= maxLevel) {
        const questions = this.getAvailableQuestionsForLevel(this.currentLevel + offset);
        if (questions.length > 0) {
          console.log(`📈 Using question from higher level ${this.currentLevel + offset}`);
          this.usedQuestions.add(questions[0].el_ID);
          return questions[0];
        }
      }
      
      // Niveau inférieur
      if (this.currentLevel - offset >= 1) {
        const questions = this.getAvailableQuestionsForLevel(this.currentLevel - offset);
        if (questions.length > 0) {
          console.log(`📉 Using question from lower level ${this.currentLevel - offset}`);
          this.usedQuestions.add(questions[0].el_ID);
          return questions[0];
        }
      }
    }

    console.error('❌ No more questions available in any level');
    return null;
  }

  /**
   * Traite la réponse de l'étudiant et détermine la prochaine étape
   * @param {Object} question La question qui a été répondue
   * @param {boolean} isCorrect Si la réponse est correcte
   * @param {*} answer La réponse donnée
   * @param {number} score Le score obtenu
   */
  processAnswer(question, isCorrect, answer, score) {
    // Enregistrer dans l'historique
    const historyEntry = {
      questionId: question.el_ID,
      level: this.currentLevel,
      isCorrect,
      answer,
      score,
      timestamp: new Date().toISOString()
    };
    
    this.questionHistory.push(historyEntry);

    console.log(`📝 Answer processed:`, {
      questionId: question.el_ID,
      level: this.currentLevel,
      isCorrect,
      score,
      historyLength: this.questionHistory.length
    });

    // Mettre à jour les compteurs de niveau
    if (isCorrect) {
      this.incrementLevelSuccess(this.currentLevel);
      this.adjustLevelAfterSuccess();
    } else {
      this.incrementLevelFailure(this.currentLevel);
      this.adjustLevelAfterFailure();
    }

    // Vérifier les conditions d'arrêt
    this.checkStoppingConditions();

    return {
      levelChanged: true,
      newLevel: this.currentLevel,
      isCompleted: this.isCompleted,
      completionReason: this.completionReason,
      totalSuccesses: this.getTotalSuccesses(),
      totalFailures: this.getTotalFailures(),
      questionsAnswered: this.questionHistory.length
    };
  }

  /**
   * Ajuste le niveau après une réponse correcte
   */
  adjustLevelAfterSuccess() {
    if (this.currentLevel < this.levels.length) {
      this.currentLevel++;
      console.log(`📈 Level increased to ${this.currentLevel} after success`);
    } else {
      console.log(`🏆 Already at maximum level ${this.currentLevel}`);
    }
  }

  /**
   * Ajuste le niveau après une réponse incorrecte
   */
  adjustLevelAfterFailure() {
    if (this.currentLevel > 1) {
      this.currentLevel--;
      console.log(`📉 Level decreased to ${this.currentLevel} after failure`);
    } else {
      console.log(`⬇️ Already at minimum level ${this.currentLevel}`);
    }
  }

  /**
   * Incrémente le compteur de réussites pour un niveau
   */
  incrementLevelSuccess(level) {
    if (!this.levelSuccesses.has(level)) {
      this.levelSuccesses.set(level, 0);
    }
    this.levelSuccesses.set(level, this.levelSuccesses.get(level) + 1);
    console.log(`✅ Level ${level} successes: ${this.levelSuccesses.get(level)}`);
  }

  /**
   * Incrémente le compteur d'échecs pour un niveau
   */
  incrementLevelFailure(level) {
    if (!this.levelFailures.has(level)) {
      this.levelFailures.set(level, 0);
    }
    this.levelFailures.set(level, this.levelFailures.get(level) + 1);
    console.log(`❌ Level ${level} failures: ${this.levelFailures.get(level)}`);
  }

  /**
   * Vérifie les conditions d'arrêt du test
   */
  checkStoppingConditions() {
    // Condition 1: Seuil d'échec au niveau 1
    if (this.currentLevel === 1 && this.levelFailures.get(1) >= this.failureThreshold) {
      this.isCompleted = true;
      this.completionReason = 'failure_threshold_reached';
      console.log(`🛑 Test stopped: Failure threshold (${this.failureThreshold}) reached at level 1`);
      return;
    }

    // Condition 2: Seuil de confirmation atteint pour le niveau actuel
    const currentLevelSuccesses = this.levelSuccesses.get(this.currentLevel) || 0;
    if (currentLevelSuccesses >= this.confirmationThreshold) {
      this.isCompleted = true;
      this.completionReason = 'confirmation_threshold_reached';
      console.log(`🎉 Test stopped: Level ${this.currentLevel} confirmed with ${currentLevelSuccesses}/${this.confirmationThreshold} successes`);
      return;
    }

    // Condition 3: Plus de questions disponibles
    if (this.usedQuestions.size >= this.elements.length) {
      this.isCompleted = true;
      this.completionReason = 'no_more_questions';
      console.log('🛑 Test stopped: No more questions available');
      return;
    }

    console.log(`▶️ Test continues: Level ${this.currentLevel} has ${currentLevelSuccesses}/${this.confirmationThreshold} successes`);
  }

  /**
   * Calcule le nombre total de réussites
   */
  getTotalSuccesses() {
    let total = 0;
    for (const count of this.levelSuccesses.values()) {
      total += count;
    }
    return total;
  }

  /**
   * Calcule le nombre total d'échecs
   */
  getTotalFailures() {
    let total = 0;
    for (const count of this.levelFailures.values()) {
      total += count;
    }
    return total;
  }

  /**
   * Récupère les statistiques du niveau actuel
   */
  getCurrentLevelStats() {
    return {
      level: this.currentLevel,
      successes: this.levelSuccesses.get(this.currentLevel) || 0,
      failures: this.levelFailures.get(this.currentLevel) || 0,
      confirmationThreshold: this.confirmationThreshold,
      failureThreshold: this.failureThreshold
    };
  }

  /**
   * Génère un rapport final du parcours de l'étudiant
   */
  generateFinalReport() {
    const totalQuestions = this.questionHistory.length;
    const totalSuccesses = this.getTotalSuccesses();
    const totalFailures = this.getTotalFailures();
    const successRate = totalQuestions > 0 ? (totalSuccesses / totalQuestions) * 100 : 0;

    // Calculer le niveau final estimé
    const finalLevel = this.calculateFinalLevel();

    return {
      // Statistiques générales
      totalQuestionsAnswered: totalQuestions,
      totalSuccesses,
      totalFailures,
      successRate: Math.round(successRate * 100) / 100,
      finalLevel,
      completionReason: this.completionReason,
      
      // Détails par niveau
      levelBreakdown: this.generateLevelBreakdown(),
      
      // Historique complet
      questionHistory: this.questionHistory,
      
      // Paramètres du test
      testParameters: {
        confirmationThreshold: this.confirmationThreshold,
        failureThreshold: this.failureThreshold,
        startingLevel: this.calculateStartingLevel(),
        totalLevels: this.levels.length
      }
    };
  }

  /**
   * Calcule le niveau final estimé de l'étudiant
   */
  calculateFinalLevel() {
    // Si arrêt par échec au niveau 1, niveau estimé = 1
    if (this.completionReason === 'failure_threshold_reached') {
      return 1;
    }

    // Si arrêt par confirmation, prendre le niveau le plus élevé avec succès
    let maxLevelWithSuccess = 1;
    for (const [level, successes] of this.levelSuccesses.entries()) {
      if (successes > 0 && level > maxLevelWithSuccess) {
        maxLevelWithSuccess = level;
      }
    }

    return maxLevelWithSuccess;
  }

  /**
   * Génère un rapport détaillé par niveau
   */
  generateLevelBreakdown() {
    const breakdown = [];
    
    for (let i = 1; i <= this.levels.length; i++) {
      breakdown.push({
        level: i,
        successes: this.levelSuccesses.get(i) || 0,
        failures: this.levelFailures.get(i) || 0,
        questionsAttempted: (this.levelSuccesses.get(i) || 0) + (this.levelFailures.get(i) || 0)
      });
    }

    return breakdown;
  }

  /**
   * Vérifie si le test est terminé
   */
  isTestCompleted() {
    return this.isCompleted;
  }

  /**
   * Récupère l'état actuel du moteur CAT
   */
  getCurrentState() {
    return {
      currentLevel: this.currentLevel,
      questionsAnswered: this.questionHistory.length,
      totalSuccesses: this.getTotalSuccesses(),
      totalFailures: this.getTotalFailures(),
      usedQuestions: Array.from(this.usedQuestions),
      isCompleted: this.isCompleted,
      completionReason: this.completionReason
    };
  }
}